/**
 * Engage Personas Browser
 *
 * Browse and view generated Engage personas.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { api } from '@personic/shared'
import PersonaPreview from '@/components/engage/PersonaPreview'

interface Persona {
  id: string
  profile_id: string
  persona_document: {
    summary: string
    communication: {
      preferred_style: string
      formality_level: number
      verbosity: string
      technical_depth: string
    }
    emotional_state?: {
      baseline_tendencies: string[]
      current_indicators: string[]
    }
    interaction_hints?: {
      do: string[]
      avoid: string[]
    }
    behavioral_patterns?: {
      decision_style: string
      information_processing: string
      conflict_approach: string
    }
    confidence: string
  }
  confidence_level: string
  maturity_score?: number
  version: number
  generated_at: string
}

export default function EngagePersonas() {
  const [selectedPersona, setSelectedPersona] = useState<Persona | null>(null)
  const [confidenceFilter, setConfidenceFilter] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')

  const { data: profiles, isLoading } = useQuery({
    queryKey: ['engage-profiles-with-personas'],
    queryFn: async () => {
      // First get profiles, then fetch their personas
      const profilesRes = await api.get('/v3/profiles?limit=50')
      const profiles = profilesRes.data.profiles || []

      // Fetch personas for each profile
      const profilesWithPersonas = await Promise.all(
        profiles.map(async (profile: { id: string; name?: string; email?: string }) => {
          try {
            const personasRes = await api.get(`/api/engage/v1/profile/${profile.id}/personas?limit=1`)
            return {
              profile,
              persona: personasRes.data[0] || null,
            }
          } catch {
            return { profile, persona: null }
          }
        })
      )

      return profilesWithPersonas.filter((p: { persona: Persona | null }) => p.persona !== null)
    },
  })

  const filteredProfiles = profiles?.filter((item: { profile: { id: string; name?: string; email?: string }; persona: Persona }) => {
    // Confidence filter
    if (confidenceFilter !== 'all' && item.persona?.confidence_level !== confidenceFilter) {
      return false
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      const profileName = item.profile.name?.toLowerCase() || ''
      const profileEmail = item.profile.email?.toLowerCase() || ''
      const summary = item.persona?.persona_document?.summary?.toLowerCase() || ''
      return profileName.includes(query) || profileEmail.includes(query) || summary.includes(query)
    }

    return true
  })

  const getConfidenceColor = (level: string) => {
    switch (level) {
      case 'mature': return 'bg-green-100 text-green-800'
      case 'established': return 'bg-blue-100 text-blue-800'
      case 'emerging': return 'bg-yellow-100 text-yellow-800'
      case 'minimal': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Engage Personas</h1>
          <p className="text-gray-600 mt-1">
            Generated customer personas for personalized interactions
          </p>
        </div>
        <div className="text-sm text-gray-500">
          {filteredProfiles?.length || 0} personas
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search personas..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-engage-primary/20 focus:border-engage-primary"
          />
        </div>
        <select
          value={confidenceFilter}
          onChange={(e) => setConfidenceFilter(e.target.value)}
          className="px-4 py-2 border border-gray-200 rounded-lg"
        >
          <option value="all">All confidence levels</option>
          <option value="mature">Mature</option>
          <option value="established">Established</option>
          <option value="emerging">Emerging</option>
          <option value="minimal">Minimal</option>
        </select>
      </div>

      {/* Content */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Personas Grid */}
        <div className="lg:col-span-2">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">Loading personas...</div>
            </div>
          ) : filteredProfiles?.length === 0 ? (
            <div className="card p-12 text-center">
              <div className="text-6xl mb-4">🎭</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Personas Found</h3>
              <p className="text-gray-500">
                Personas are generated automatically during Engage sessions.
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              <AnimatePresence>
                {filteredProfiles?.map((item: { profile: { id: string; name?: string; email?: string }; persona: Persona }, index: number) => (
                  <motion.button
                    key={item.profile.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => setSelectedPersona(item.persona)}
                    className={`card p-4 text-left hover:shadow-lg transition-shadow ${
                      selectedPersona?.id === item.persona?.id ? 'ring-2 ring-engage-primary' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-engage-primary/10 flex items-center justify-center">
                          <span className="text-engage-primary font-semibold">
                            {item.profile.name?.charAt(0) || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {item.profile.name || 'Unnamed Profile'}
                          </p>
                          <p className="text-xs text-gray-500">
                            v{item.persona?.version || 1}
                          </p>
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${getConfidenceColor(item.persona?.confidence_level || 'minimal')}`}>
                        {item.persona?.confidence_level || 'minimal'}
                      </span>
                    </div>

                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                      {item.persona?.persona_document?.summary || 'No summary available'}
                    </p>

                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>
                        {item.persona?.persona_document?.communication?.preferred_style || 'balanced'}
                      </span>
                      <span>•</span>
                      <span>
                        {new Date(item.persona?.generated_at || '').toLocaleDateString()}
                      </span>
                    </div>
                  </motion.button>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* Persona Detail Panel */}
        <div className="lg:col-span-1">
          <div className="card sticky top-6">
            {selectedPersona ? (
              <PersonaPreview persona={selectedPersona} />
            ) : (
              <div className="p-8 text-center">
                <div className="text-4xl mb-3 opacity-50">👈</div>
                <p className="text-gray-500">
                  Select a persona to view details
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
